import { Box, Container, Typography, Stack } from '@mui/material';
import { motion } from 'framer-motion';

export default function SectionHero({ title, subtitle, variant = 'solid', image }) {
  const base = {
    minHeight: { xs: 260, sm: 320, md: 520 },
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    py: { xs: 4, md: 0 },
  };

  const variants = {
    solid: {
      bgcolor: 'primary.main',
      color: '#111',
    },
    grid: {
      bgcolor: '#fff',
      color: 'secondary.main',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background:
          'radial-gradient(circle at 20% 10%, rgba(255,106,0,0.15) 0 30%, transparent 30% 100%), linear-gradient(90deg, rgba(17,17,17,0.06) 1px, transparent 1px), linear-gradient(0deg, rgba(17,17,17,0.06) 1px, transparent 1px)',
        backgroundSize: 'auto, 36px 36px, 36px 36px',
      },
    },
    diagonal: {
      bgcolor: '#111',
      color: '#fff',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background:
          'linear-gradient(120deg, rgba(255,106,0,0.9) 0 30%, transparent 30% 100%)',
      },
    },
    image: {
      color: '#fff',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        backgroundImage: `url(${image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'brightness(0.6) saturate(1.05)',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background:
          'linear-gradient(180deg, rgba(17,17,17,0.2), rgba(17,17,17,0.4))',
      },
    },
    imageStripes: {
      color: '#fff',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        backgroundImage: `url(${image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'brightness(0.55) saturate(1.1)',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background:
          'linear-gradient(120deg, rgba(255,106,0,0.5) 0 20%, transparent 20% 100%)',
        mixBlendMode: 'overlay',
      },
    },
    dots: {
      bgcolor: '#fff',
      color: 'secondary.main',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background:
          'radial-gradient(#ddd 1px, transparent 1px) 0 0/14px 14px, linear-gradient(90deg, rgba(255,106,0,0.08), transparent)',
      },
    },
    scan: {
      bgcolor: '#111',
      color: '#fff',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background:
          'radial-gradient(circle at 20% 10%, rgba(255,106,0,0.15) 0 35%, transparent 35% 100%)',
      },
    },
    map: {
      bgcolor: '#fff',
      color: 'secondary.main',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background:
          'radial-gradient(500px 260px at 10% 40%, rgba(255,106,0,0.12), transparent), radial-gradient(400px 220px at 85% 70%, rgba(17,17,17,0.06), transparent)',
      },
    },
    contactCircles: {
      bgcolor: '#fff',
      color: 'secondary.main',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background:
          'radial-gradient(600px 300px at 20% 30%, rgba(255,106,0,0.15), transparent), radial-gradient(500px 260px at 80% 60%, rgba(17,17,17,0.04), transparent)',
      },
    },
  };

  return (
    <Box sx={{ ...base, ...(variants[variant] || variants.solid) }}>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h1"
            sx={{
              fontWeight: 900,
              lineHeight: { xs: 1.05, md: 0.95 },
              fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
              wordBreak: 'break-word',
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              sx={{
                mt: 1.5,
                fontWeight: 600,
                opacity: 0.9,
                fontSize: { xs: '0.95rem', sm: '1rem', md: '1.125rem' },
                lineHeight: 1.6,
                maxWidth: { xs: '100%', md: 720 },
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Container>
      {variant === 'scan' ? (
        <Box
          component={motion.div}
          initial={{ y: '-120%' }}
          animate={{ y: '120%' }}
          transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            height: 120,
            background:
              'linear-gradient(180deg, transparent, rgba(255,255,255,0.06), transparent)',
          }}
        />
      ) : null}
      {variant === 'imageStripes' ? (
        <>
          <Box
            component={motion.div}
            initial={{ x: 80, opacity: 0 }}
            animate={{ x: 0, opacity: 0.5 }}
            transition={{ duration: 0.8 }}
            sx={{
              position: 'absolute',
              right: -40,
              top: 0,
              bottom: 0,
              width: 140,
              background:
                'repeating-linear-gradient( to bottom, rgba(255,255,255,0.2) 0 6px, transparent 6px 16px )',
              transform: 'skewX(-8deg)',
            }}
          />
        </>
      ) : null}
      {variant === 'contactCircles' ? (
        <>
          <Box
            component={motion.div}
            animate={{ x: [0, 10, 0], y: [0, -6, 0] }}
            transition={{ duration: 8, repeat: Infinity }}
            sx={{ position: 'absolute', top: 60, left: '15%', width: 160, height: 160, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)' }}
          />
          <Box
            component={motion.div}
            animate={{ x: [0, -8, 0], y: [0, 6, 0] }}
            transition={{ duration: 10, repeat: Infinity }}
            sx={{ position: 'absolute', bottom: 40, right: '10%', width: 220, height: 220, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.15)' }}
          />
        </>
      ) : null}
    </Box>
  );
}
